// Loans API (LOANS spec §2): provisional, mock-first. Keep loans in this one
// module; switch MOCK_LOANS off when the backend ships its endpoints.
import type {
  ApplyInput,
  CibilResult,
  Holding,
  KeyFactStatement,
  LoanAlert,
  LoanProduct,
  LoanRecord,
  LoanSummary,
  PreApprovedOffer,
  RepaymentMethod,
  ScheduleEntry,
} from '@/types/loan.types';
import { ApiError, createIdempotencyKey, get, post, put } from './client';
import { MOCK_LOANS, REAL_API_ENABLED } from './flags';
import * as mock from '@/mock/handlers/loans';

const notAvailable = (): never => {
  throw new ApiError('NOT_AVAILABLE', 'The loans API is not available yet.');
};

const loanPath = (path: string, id: string): string =>
  `${path}/${encodeURIComponent(id)}`;

export type LoanControllerInput = Record<string, unknown>;
export type LoanControllerResult = Record<string, unknown>;

export interface LoanApplicationInput extends LoanControllerInput {
  product: string;
  amountPaise: number;
  tenureMonths: number;
  emiDay: number;
  repaymentMethod: string;
  rateType: string;
  purpose: string;
  insuranceOpted: boolean;
  collateralValuePaise?: number;
  cibilConsentGiven: boolean;
}

export interface CibilCheckInput {
  product: string;
  rateType: string;
  consentGiven: boolean;
}

export interface InsuranceInput {
  opted: boolean;
}

export interface LoanControllerListItem extends LoanControllerResult {
  id: string;
}

export interface LoanProductDetails extends LoanControllerResult {
  code: string;
  name: string;
}

export interface LoanControllerQueryResult extends LoanControllerResult {
  [key: string]: unknown;
}

export interface LoanStatementEntry extends LoanControllerResult {
  [key: string]: unknown;
}

export interface LoanScheduleEntry extends LoanControllerResult {
  [key: string]: unknown;
}

export interface LoanApplicationDetails extends LoanControllerResult {
  id: string;
}

export interface LoanApplicationKfs extends LoanControllerResult {
  [key: string]: unknown;
}

export interface AdminLoanProduct {
  createdAt: string;
  updatedAt: string;
  createdBy: string;
  updatedBy: string;
  version: number;
  code: string;
  name: string;
  minAmountPaise: number;
  maxAmountPaise: number;
  minRatePercent: number;
  maxRatePercent: number;
  minTenureMonths: number;
  maxTenureMonths: number;
  incomeFloorPaise: number;
  coolingOffDays: number;
  interestMethod: string;
  dayCountConvention: string;
  compounding: string;
  processingFeePercent: number;
  gstPercent: number;
  foreclosureChargePercent: number;
  foreclosureLockInMonths: number;
  partPrepayChargePercent: number;
  minPartPaymentPaise: number;
  lateChargePaise: number;
  gracePeriodDays: number;
  revolving: boolean;
  purposes: string;
  secured: boolean;
  collateralLabel: string;
  maxLtvPercent: number;
  active: boolean;
}

export type UpdateAdminLoanProductInput = Partial<Omit<AdminLoanProduct,
  'createdAt' | 'updatedAt' | 'createdBy' | 'updatedBy' | 'version' | 'code'
>>;

const adminLoanProductsAvailable = (): boolean => REAL_API_ENABLED;

export const getAdminLoanProducts = (): Promise<AdminLoanProduct[]> =>
  adminLoanProductsAvailable()
    ? get<AdminLoanProduct[]>('/admin/loan-products')
    : notAvailable();

export const getAdminLoanProduct = (code: string): Promise<AdminLoanProduct> =>
  adminLoanProductsAvailable()
    ? get<AdminLoanProduct>(`/admin/loan-products/${encodeURIComponent(code)}`)
    : notAvailable();

export const updateAdminLoanProduct = (
  code: string,
  input: UpdateAdminLoanProductInput,
): Promise<AdminLoanProduct> =>
  adminLoanProductsAvailable()
    ? put<AdminLoanProduct>(`/admin/loan-products/${encodeURIComponent(code)}`, input)
    : notAvailable();

export const updateLoanRepaymentMethod = (
  loanId: string,
  input: LoanControllerInput,
): Promise<LoanControllerResult> =>
  put<LoanControllerResult>(`${loanPath('/loans', loanId)}/repayment-method`, input);

export const updateLoanEmiDay = (
  loanId: string,
  input: LoanControllerInput,
): Promise<LoanControllerResult> =>
  put<LoanControllerResult>(`${loanPath('/loans', loanId)}/emi-day`, input);

export const setLoanDrawingEnabledApi = (
  loanId: string,
  enabled: boolean,
): Promise<LoanControllerResult> =>
  put<LoanControllerResult>(`${loanPath('/loans', loanId)}/drawing-enabled`, undefined, {
    params: { enabled },
  });

export const updateLoanApplicationInsurance = (
  applicationId: string,
  input: InsuranceInput,
): Promise<LoanControllerResult> =>
  put<LoanControllerResult>(`${loanPath('/loans/applications', applicationId)}/insurance`, input);

const loanMutation = (
  path: string,
  input: LoanControllerInput = {},
  idempotencyKey = createIdempotencyKey(),
): Promise<LoanControllerResult> =>
  post<LoanControllerResult>(path, input, {
    headers: { 'Idempotency-Key': idempotencyKey },
  });

export const topUpLoanApi = (loanId: string, input: LoanControllerInput, idempotencyKey?: string) =>
  loanMutation(`${loanPath('/loans', loanId)}/top-up`, input, idempotencyKey);
export const repayLoanLimit = (loanId: string, input: LoanControllerInput, idempotencyKey?: string) =>
  loanMutation(`${loanPath('/loans', loanId)}/repay-limit`, input, idempotencyKey);
export const payLoanEmi = (loanId: string, input: LoanControllerInput = {}, idempotencyKey?: string) =>
  loanMutation(`${loanPath('/loans', loanId)}/pay-emi`, input, idempotencyKey);
export const partPrepayLoan = (loanId: string, input: LoanControllerInput, idempotencyKey?: string) =>
  loanMutation(`${loanPath('/loans', loanId)}/part-prepay`, input, idempotencyKey);
export const forecloseLoanApi = (loanId: string, input: LoanControllerInput = {}, idempotencyKey?: string) =>
  loanMutation(`${loanPath('/loans', loanId)}/foreclose`, input, idempotencyKey);
export const drawLoan = (loanId: string, input: LoanControllerInput, idempotencyKey?: string) =>
  loanMutation(`${loanPath('/loans', loanId)}/draw`, input, idempotencyKey);
export const exitLoanCoolingOff = (loanId: string, input: LoanControllerInput, idempotencyKey?: string) =>
  loanMutation(`${loanPath('/loans', loanId)}/cooling-off-exit`, input, idempotencyKey);
export const refundBnplLoan = (loanId: string, input: LoanControllerInput, idempotencyKey?: string) =>
  loanMutation(`${loanPath('/loans', loanId)}/bnpl/refund`, input, idempotencyKey);
export const purchaseBnplLoan = (loanId: string, input: LoanControllerInput, idempotencyKey?: string) =>
  loanMutation(`${loanPath('/loans', loanId)}/bnpl/purchase`, input, idempotencyKey);

export const checkLoanCibil = (input: CibilCheckInput): Promise<LoanControllerResult> =>
  post<LoanControllerResult>('/loans/cibil-check', input);

export const listLoanApplications = (): Promise<LoanControllerListItem[]> =>
  get<LoanControllerListItem[]>('/loans/applications');
export const createLoanApplication = (input: LoanApplicationInput): Promise<LoanControllerResult> =>
  post<LoanControllerResult>('/loans/applications', input);
export const acceptLoanApplicationKfs = (applicationId: string): Promise<LoanControllerResult> =>
  post<LoanControllerResult>(`${loanPath('/loans/applications', applicationId)}/kfs/accept`);
export const disburseLoanApplication = (
  applicationId: string,
  input: LoanControllerInput = {},
  idempotencyKey?: string,
): Promise<LoanControllerResult> =>
  loanMutation(`${loanPath('/loans/applications', applicationId)}/disburse`, input, idempotencyKey);
export const cancelLoanApplication = (applicationId: string): Promise<LoanControllerResult> =>
  post<LoanControllerResult>(`${loanPath('/loans/applications', applicationId)}/cancel`);

export const listDisbursedLoans = (): Promise<LoanControllerListItem[]> => get<LoanControllerListItem[]>('/loans');
export const getLoanDetails = (loanId: string): Promise<LoanControllerResult> =>
  get<LoanControllerResult>(loanPath('/loans', loanId));
export const getLoanStatement = (loanId: string): Promise<LoanControllerListItem[]> =>
  get<LoanControllerListItem[]>(`${loanPath('/loans', loanId)}/statement`);
export const getLoanRepaymentSchedule = (loanId: string): Promise<LoanControllerListItem[]> =>
  get<LoanControllerListItem[]>(`${loanPath('/loans', loanId)}/schedule`);
export const getLoansSummary = (): Promise<LoanControllerResult> => get<LoanControllerResult>('/loans/summary');
export const listLoanProducts = (): Promise<LoanProductDetails[]> => get<LoanProductDetails[]>('/loans/products');
export const getLoanOffers = (): Promise<LoanControllerResult> => get<LoanControllerResult>('/loans/offers');
export const getLoanApplication = (applicationId: string): Promise<LoanApplicationDetails> =>
  get<LoanApplicationDetails>(loanPath('/loans/applications', applicationId));
export const getLoanApplicationKfs = (applicationId: string): Promise<LoanApplicationKfs> =>
  get<LoanApplicationKfs>(`${loanPath('/loans/applications', applicationId)}/kfs`);

// Products and offers
export const getProducts = (): Promise<LoanProduct[]> => (MOCK_LOANS ? mock.getProducts() : notAvailable());
export const getPreApprovedOffers = (): Promise<PreApprovedOffer[]> =>
  MOCK_LOANS ? mock.getPreApprovedOffers() : notAvailable();
export const getLoanSummary = (): Promise<LoanSummary> => (MOCK_LOANS ? mock.getLoanSummary() : notAvailable());

// Application
export const checkCibil = (productId: string, consent: boolean): Promise<CibilResult> =>
  MOCK_LOANS ? mock.checkCibil(productId, consent) : notAvailable();
export const applyForLoan = (input: ApplyInput): Promise<LoanRecord> =>
  MOCK_LOANS ? mock.applyForLoan(input) : notAvailable();
export const getApplications = (): Promise<LoanRecord[]> =>
  MOCK_LOANS ? mock.getApplications() : notAvailable();
export const getKeyFactStatement = (loanId: string): Promise<KeyFactStatement> =>
  MOCK_LOANS ? mock.getKeyFactStatement(loanId) : notAvailable();
export const acknowledgeKfs = (loanId: string): Promise<void> =>
  MOCK_LOANS ? mock.acknowledgeKfs(loanId) : notAvailable();
export const submitDocuments = (loanId: string, documents: string[]): Promise<void> =>
  MOCK_LOANS ? mock.submitDocuments(loanId, documents) : notAvailable();
export const setInsurance = (loanId: string, opted: boolean): Promise<LoanRecord> =>
  MOCK_LOANS ? mock.setInsurance(loanId, opted) : notAvailable();
export const cancelApplication = (loanId: string): Promise<void> =>
  MOCK_LOANS ? mock.cancelApplication(loanId) : notAvailable();
export const disburseLoan = (loanId: string): Promise<LoanRecord> =>
  MOCK_LOANS ? mock.disburseLoan(loanId) : notAvailable();

// Running loan
export const getLoans = (): Promise<LoanRecord[]> => (MOCK_LOANS ? mock.getLoans() : notAvailable());
export const getLoan = (loanId: string): Promise<LoanRecord> =>
  MOCK_LOANS ? mock.getLoan(loanId) : notAvailable();
export const getSchedule = (loanId: string): Promise<ScheduleEntry[]> =>
  MOCK_LOANS ? mock.getSchedule(loanId) : notAvailable();
export const getLoanAlerts = (): Promise<LoanAlert[]> => (MOCK_LOANS ? mock.getAlerts() : notAvailable());
export const payEmiNow = (loanId: string): Promise<LoanRecord> =>
  MOCK_LOANS ? mock.payEmiNow(loanId) : notAvailable();
export const exitDuringCoolingOff = (loanId: string): Promise<LoanRecord> =>
  MOCK_LOANS ? mock.exitDuringCoolingOff(loanId) : notAvailable();
export const forecloseLoan = (loanId: string): Promise<LoanRecord> =>
  MOCK_LOANS ? mock.forecloseLoan(loanId) : notAvailable();
export const partPrepay = (loanId: string, amountRupees: number, mode: 'REDUCE_EMI' | 'REDUCE_TENURE'): Promise<LoanRecord> =>
  MOCK_LOANS ? mock.partPrepay(loanId, amountRupees, mode) : notAvailable();
export const topUpLoan = (loanId: string, amountRupees: number): Promise<LoanRecord> =>
  MOCK_LOANS ? mock.topUpLoan(loanId, amountRupees) : notAvailable();
export const changeRepaymentMethod = (loanId: string, method: RepaymentMethod): Promise<void> =>
  MOCK_LOANS ? mock.changeRepaymentMethod(loanId, method) : notAvailable();
export const changeEmiDay = (loanId: string, day: number): Promise<void> =>
  MOCK_LOANS ? mock.changeEmiDay(loanId, day) : notAvailable();

// Revolving
export const drawFromLimit = (loanId: string, amountRupees: number): Promise<LoanRecord> =>
  MOCK_LOANS ? mock.drawFromLimit(loanId, amountRupees) : notAvailable();
export const repayToLimit = (loanId: string, amountRupees: number): Promise<LoanRecord> =>
  MOCK_LOANS ? mock.repayToLimit(loanId, amountRupees) : notAvailable();
export const setDrawingEnabled = (loanId: string, enabled: boolean): Promise<void> =>
  MOCK_LOANS ? mock.setDrawingEnabled(loanId, enabled) : notAvailable();
export const increaseLoanLimit = (loanId: string, newLimitRupees: number, consent: boolean): Promise<LoanRecord> =>
  MOCK_LOANS ? mock.increaseLoanLimit(loanId, newLimitRupees, consent) : notAvailable();

export const requestLoanLimitIncrease = (
  loanId: string,
  requestedLimitPaise: number,
): Promise<Record<string, unknown>> =>
  REAL_API_ENABLED
    ? post<Record<string, unknown>>(`/loans/${encodeURIComponent(loanId)}/limit-increase`, undefined, {
        params: { requestedLimitPaise },
      })
    : notAvailable();

export const uploadLoanApplicationDocument = (
  applicationId: string,
  documentType: string,
  file: File,
): Promise<Record<string, unknown>> => {
  if (!REAL_API_ENABLED) return notAvailable();
  const formData = new FormData();
  formData.append('file', file);
  return post<Record<string, unknown>>(
    `/loans/applications/${encodeURIComponent(applicationId)}/documents`,
    formData,
    { params: { documentType } },
  );
};
export const bnplPurchase = (loanId: string, partnerCode: string, purchaseCode: string, amountRupees: number): Promise<LoanRecord> =>
  MOCK_LOANS ? mock.bnplPurchase(loanId, partnerCode, purchaseCode, amountRupees) : notAvailable();
export const bnplRefund = (loanId: string, purchaseId: string, amountRupees: number): Promise<LoanRecord> =>
  MOCK_LOANS ? mock.bnplRefund(loanId, purchaseId, amountRupees) : notAvailable();

// Collateral
export const getSecurityHoldings = (): Promise<Array<Holding & { pledged: boolean }>> =>
  MOCK_LOANS ? mock.getSecurityHoldings() : notAvailable();
export const pledgeMore = (loanId: string, holdingIds: string[]): Promise<LoanRecord> =>
  MOCK_LOANS ? mock.pledgeMore(loanId, holdingIds) : notAvailable();

export const pledgeLoanSecurity = (
  loanId: string,
  securityType: string,
  details?: string,
): Promise<Record<string, unknown>> =>
  REAL_API_ENABLED
    ? post<Record<string, unknown>>(`/loans/${encodeURIComponent(loanId)}/pledge`, undefined, {
        params: { securityType, ...(details ? { details } : {}) },
      })
    : notAvailable();
export const goldRatePerGramPaise = (): Promise<number> =>
  MOCK_LOANS ? mock.goldRatePerGramPaise() : notAvailable();

// Programme
export const deregisterInstantLoan = (): Promise<void> =>
  MOCK_LOANS ? mock.deregisterInstantLoan() : notAvailable();

export const registerInstantLoan = (): Promise<void> =>
  MOCK_LOANS ? mock.registerInstantLoan() : notAvailable();

// Review-screen figures helper (mock-side, shared maths)
export { figuresFor } from '@/mock/handlers/loans';
